// Per-shore + per-hub-location matrix editor for `Model.costPerRole`.
//
// Tabs are: Onshore, then one tab per `OgsHub` (e.g. Mexico, Lebanon, India
// (OGS)). The hub-tab structure mirrors the "Hub locations %" tab so the
// names stay consistent with the intake's OGS card and the staff plan's
// hub picker. Onshore stays as a single shore with no hub breakdown.
//
// Engine consumption: when the scenario picks a hub via `profile.ogs.{shore}.
// hubId`, that hub's row is used; otherwise the engine falls back to the
// first hub of that shore. See `lookupCostPerRole` in table-lookup.ts.

import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Building2, MapPin } from 'lucide-react';
import { modelCurrency } from '@rc/engine';
import type { CostPerRoleRow, LevelId, Model, OgsHub } from '@rc/engine/types';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

interface OnshoreTab {
  kind: 'onshore';
}
interface HubTab {
  kind: 'hub';
  hub: OgsHub;
}
type Tab = OnshoreTab | HubTab;

export default function CostPerRoleMatrix({ model, onChange }: Props) {
  const hubs = useMemo(() => model.ogsHubs ?? [], [model.ogsHubs]);
  const tabs = useMemo<Tab[]>(
    () => [{ kind: 'onshore' }, ...hubs.map((h) => ({ kind: 'hub' as const, hub: h }))],
    [hubs],
  );

  const tabKey = (t: Tab) => (t.kind === 'onshore' ? 'onshore' : `hub:${t.hub.id}`);
  const [activeKey, setActiveKey] = useState<string>(() => tabKey(tabs[0]));

  // Keep the active tab valid if hubs are added/removed.
  useEffect(() => {
    if (!tabs.some((t) => tabKey(t) === activeKey)) {
      setActiveKey(tabKey(tabs[0]));
    }
  }, [tabs, activeKey]);

  const activeTab = tabs.find((t) => tabKey(t) === activeKey) ?? tabs[0];
  const base = modelCurrency(model).base;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 max-w-2xl">
        Fully-loaded annual cost per FTE, in <strong>{base}</strong>. Stage 5 multiplies these by
        the per-cell shore-split FTE — when a scenario picks a specific hub in
        the intake's OGS card, that hub's costs are used; otherwise the
        engine falls back to the first hub of the shore.
      </p>

      <div className="flex flex-wrap gap-1.5 border-b border-slate-200 -mb-px">
        {tabs.map((t) => (
          <CostTabButton
            key={tabKey(t)}
            tab={t}
            active={tabKey(t) === activeKey}
            onClick={() => setActiveKey(tabKey(t))}
          />
        ))}
      </div>

      <CostMatrix model={model} onChange={onChange} tab={activeTab} />
    </div>
  );
}

function CostTabButton({
  tab,
  active,
  onClick,
}: {
  tab: Tab;
  active: boolean;
  onClick: () => void;
}) {
  if (tab.kind === 'onshore') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          'px-3 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors flex items-center gap-1.5',
          active
            ? 'border-brand-purple-500 text-brand-purple-700 bg-brand-purple-50/40'
            : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50',
        )}
      >
        <Building2
          size={12}
          className={active ? 'text-brand-purple-500' : 'text-slate-400'}
        />
        <span>Onshore</span>
      </button>
    );
  }
  const { hub } = tab;
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'px-3 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors flex items-center gap-1.5',
        active
          ? 'border-brand-purple-500 text-brand-purple-700 bg-brand-purple-50/40'
          : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50',
      )}
    >
      <MapPin
        size={12}
        className={active ? 'text-brand-purple-500' : 'text-slate-400'}
      />
      <span>{hub.label}</span>
      <span
        className={clsx(
          'pill text-[10px] ml-0.5',
          hub.shore === 'nearshore'
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-amber-50 text-amber-800',
        )}
      >
        {hub.shore}
      </span>
    </button>
  );
}

function CostMatrix({
  model,
  onChange,
  tab,
}: {
  model: Model;
  onChange: (m: Model) => void;
  tab: Tab;
}) {
  const pairs = useMemo(() => {
    const seen = new Set<string>();
    const out: { disciplineId: string; channelId: string }[] = [];
    for (const sp of model.spendPerFte || []) {
      const k = `${sp.disciplineId}::${sp.channelId}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ disciplineId: sp.disciplineId, channelId: sp.channelId });
    }
    return out;
  }, [model.spendPerFte]);

  const levels = model.levels || [];
  const rows = model.costPerRole || [];

  // shore + hubId for the active tab
  const tabShore = tab.kind === 'onshore' ? 'onshore' : tab.hub.shore;
  const tabHubId = tab.kind === 'hub' ? tab.hub.id : undefined;

  const matchesTab = (r: CostPerRoleRow) => {
    if (r.shore !== tabShore) return false;
    if (tab.kind === 'onshore') return !r.hubId;
    return r.hubId === tabHubId;
  };

  const findRow = (
    disciplineId: string,
    channelId: string,
    levelId: LevelId,
  ): CostPerRoleRow | undefined =>
    rows.find(
      (r) =>
        matchesTab(r) &&
        r.disciplineId === disciplineId &&
        r.channelId === channelId &&
        r.levelId === levelId,
    );

  const setCost = (
    disciplineId: string,
    channelId: string,
    levelId: LevelId,
    annualCost: number,
  ) => {
    const next = [...rows];
    const idx = next.findIndex(
      (r) =>
        matchesTab(r) &&
        r.disciplineId === disciplineId &&
        r.channelId === channelId &&
        r.levelId === levelId,
    );
    if (idx >= 0) {
      next[idx] = { ...next[idx], annualCost };
    } else {
      next.push({
        shore: tabShore,
        hubId: tabHubId,
        disciplineId,
        channelId,
        levelId,
        annualCost,
      });
    }
    onChange({ ...model, costPerRole: next });
  };

  const discLabel = (id: string) =>
    (model.disciplines || []).find((d) => d.id === id)?.label || id;
  const chanLabel = (id: string) =>
    id === 'all' ? 'All channels' : (model.channels || []).find((c) => c.id === id)?.label || id;

  return (
    <div className="rounded-lg border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Discipline</th>
            <th className="text-left px-3 py-2">Channel</th>
            {levels.map((l) => (
              <th key={l.id} className="text-right px-3 py-2 tabular-nums">
                {l.id}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pairs.map(({ disciplineId, channelId }) => (
            <tr key={`${disciplineId}::${channelId}`} className="border-t border-slate-100">
              <td className="px-3 py-1.5 sticky left-0 bg-white text-slate-700 font-medium">
                {discLabel(disciplineId)}
              </td>
              <td className="px-3 py-1.5 text-slate-500 text-xs">{chanLabel(channelId)}</td>
              {levels.map((l) => {
                const cur = findRow(disciplineId, channelId, l.id)?.annualCost ?? 0;
                return (
                  <td key={l.id} className="px-1 py-1 align-middle min-w-[110px]">
                    <input
                      type="number"
                      step={1000}
                      className="input py-1 px-2 w-full text-right text-xs tabular-nums"
                      value={cur}
                      onChange={(e) =>
                        setCost(disciplineId, channelId, l.id, Number(e.target.value))
                      }
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
